import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { getUsuarios, updateUser, crearUsuario } from "../../../services/admin/usuarios_admin";
import UserFormModal from "./UserFormModal";
import { IconEdit } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";

function UsuariosPage() {
  const [usuarios, setUsuarios] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // ✅ Alert simple
  const [alert, setAlert] = useState(null); // { type, message }
  const alertTimerRef = useRef(null);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  // ✅ debounce search (300ms)
  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  // ✅ cuando cambia el search, regresamos a page 1
  useEffect(() => {
    setPage(1);
  }, [searchDebounced]);

  const cargarUsuarios = useCallback(async () => {
    try {
      // OJO: tu getUsuarios recibe (page, 20, search, search)
      const res = await getUsuarios(page, 20, searchDebounced, searchDebounced);

      setUsuarios(Array.isArray(res?.data) ? res.data : []);
      setTotalPages(Number(res?.totalPages) || 1);
    } catch (error) {
      console.error("Error loading users:", error);
      showAlert("error", error?.message || "Failed to load users.");
      setUsuarios([]);
      setTotalPages(1);
    }
  }, [page, searchDebounced, showAlert]);

  useEffect(() => {
    cargarUsuarios();
  }, [cargarUsuarios]);

  const openCreateModal = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  // ✅ success arriba, error se re-throw para que el modal lo muestre
  const handleUpdate = async (id, formData) => {
    try {
      const res = await updateUser(id, formData);
      showAlert("success", res?.message || "User updated successfully.");
      await cargarUsuarios();
      setShowModal(false);
    } catch (err) {
      console.error("Error updating user:", err);
      throw err; // 👈 el modal debe capturar y mostrar
    }
  };

  const handleCreate = async (formData) => {
    try {
      const res = await crearUsuario(formData);
      showAlert("success", res?.message || "User created successfully.");
      await cargarUsuarios();
      setShowModal(false);
    } catch (err) {
      console.error("Error creating user:", err);
      throw err; // 👈 el modal debe capturar y mostrar
    }
  };

  const nextPage = () => page < totalPages && setPage((p) => p + 1);
  const prevPage = () => page > 1 && setPage((p) => p - 1);

  const canCreate = useMemo(() => hasPerm("Create user"), []);
  const canUpdate = useMemo(() => hasPerm("Update user"), []);

  return (
    <div className="page-container">
      <style>{modernStyles}</style>

      {/* ✅ ALERT */}
      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button className="omx-alert__x" onClick={() => setAlert(null)}>
            ✕
          </button>
        </div>
      )}

      {/* HEADER */}
      <div className="header">
        <h2 className="title">Users Management</h2>

        <div className="top-bar">
          <input
            type="text"
            placeholder="Search user or email..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {canCreate && (
            <button className="btn-create" onClick={openCreateModal}>
              Create User
            </button>
          )}
        </div>
      </div>

      {/* TABLE */}
      <div className="table-card">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Lastname</th>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Date</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {usuarios.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  No users found
                </td>
              </tr>
            ) : (
              usuarios.map((u) => (
                <tr key={u.usuario_id}>
                  <td>{u.usuario_id}</td>
                  <td>{u.nombre}</td>
                  <td>{u.apellido}</td>
                  <td>{u.usuario}</td>
                  <td>{u.correo}</td>
                  <td>{u.rol}</td>
                  <td>
                    <span className={u.estado === 1 ? "status active" : "status inactive"}>
                      {u.estado === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>{u.fecha_creado?.slice(0, 10)}</td>

                  <td>
                    <div className="actions">
                      {canUpdate && (
                        <span
                          className="action-icon edit"
                          onClick={() => openEditModal(u)}
                          title="Edit user"
                        >
                          {IconEdit}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {showModal && (
        <UserFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          userData={selectedUser}
          onSubmit={(formData) =>
            selectedUser
              ? handleUpdate(selectedUser.usuario_id, formData)
              : handleCreate(formData)
          }
        />
      )}

      {/* PAGINATION */}
      <div className="pagination">
        <button onClick={prevPage} disabled={page === 1}>
          Prev
        </button>

        <span>
          Page {page} of {totalPages}
        </span>

        <button onClick={nextPage} disabled={page === totalPages}>
          Next
        </button>
      </div>
    </div>
  );
}

const modernStyles = `
.page-container {
  /* Llena el alto de la tarjeta del layout; solo el listado scrollea. */
  height: 100%;
  padding: 4px 0 0;
  font-family: 'Segoe UI';
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* Cabecera, alerta y paginacion conservan su alto; no se comprimen. */
.page-container > .header,
.page-container > .pagination,
.page-container > .omx-alert,
.page-container > .lista-recortada {
  flex: none;
}

/* ✅ ALERT */
.omx-alert{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:12px 14px;
  border-radius:12px;
  border:1px solid;
  margin: 0 0 14px 0;
  box-shadow: 0 10px 25px rgba(0,0,0,.08);
}
.omx-alert__msg{ font-size:14px; font-weight:600; }
.omx-alert__x{
  border:none;
  background:transparent;
  cursor:pointer;
  font-size:18px;
  opacity:.7;
}
.omx-alert__x:hover{ opacity:1; }

.omx-alert--success{ background:#ECFDF5; border-color:#A7F3D0; color:#065F46; }
.omx-alert--error{ background:#FEF2F2; border-color:#FECACA; color:#7F1D1D; }
.omx-alert--info{ background:#EFF6FF; border-color:#BFDBFE; color:#1E3A8A; }

.title {
  font-size: 26px;
  font-weight: 700;
  color: #2F4779;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
}

.search-input {
  padding: 10px 16px;
  width: 260px;
  border-radius: 8px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  color: #1E293B;
  font-size: 14px;
  transition: 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3B82F6;
  background: white;
}

.btn-create {
  padding: 10px 18px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.25s;
}

.btn-create:hover {
  background: #2563EB;
}

.table-card {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);

  /* min-height: 0 permite encogerse por debajo del contenido; sin eso
     el scroll interno no ocurre. */
  flex: 0 1 auto;
  min-height: 0;
  overflow: auto;
  margin-top: 20px;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table th {
  background: #F1F5F9;
  padding: 14px;
  font-size: 13px;
  color: #475569;
  text-align: left;
  border-bottom: 1px solid #E2E8F0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.modern-table td {
  padding: 14px;
  font-size: 14px;
  color: #334155;
  border-bottom: 1px solid #F1F5F9;
}

.modern-table tr:hover td {
  background: #F8FAFC;
}

.status {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.status.active {
  background: #DCFCE7;
  color: #15803D;
}

.status.inactive {
  background: #FEE2E2;
  color: #B91C1C;
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.action-icon {
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
}

.action-icon.edit:hover {
  background: #DBEAFE;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #94A3B8;
  font-size: 14px;
}

.pagination {
  margin-top: 14px;
  margin-bottom: 2px;
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
}

.pagination button {
  padding: 8px 14px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.pagination button:disabled {
  opacity: .5;
}

.pagination span {
  font-size: 14px;
  color: #475569;
}
`;

export default UsuariosPage;
